/**
 * ContextRagSettingsToggle — the chat-settings controls for run-start context
 * retrieval (the run-start-context-rag feature).
 *
 * Two rows:
 * - "Retrieve relevant context at run start" — the root-run retrieval gate
 *   (`contextRagEnabled`, platform default ON).
 * - "Also in sub-agent runs" — the opt-in child-run retrieval
 *   (`contextRagChildRunsEnabled`, platform default OFF). Only meaningful
 *   while the root gate is on; disabled with an explanatory hint otherwise
 *   (the backend enforces the same conjunction).
 *
 * PURE and PROPS-DRIVEN with zero derivation, mirroring SubagentModelToggle:
 * it owns no query, no mutation and no default — the caller decides whether a
 * flip writes a draft or PATCHes a persisted session, and the BACKEND owns the
 * default value (the client never fabricates one; an unset checkbox renders
 * the backend-resolved value from ChatSettings).
 *
 * "Takes effect on the next run": the setting is read by each run's start, so
 * flipping it mid-run governs the next run, never the one in flight.
 */

import { Tooltip } from '@localagent/ui/components/tooltip';

interface ContextRagSettingsToggleProps {
  /** Root-run retrieval gate (unset renders the backend default: on). */
  rootChecked: boolean;
  /** Child-run retrieval gate (unset renders the backend default: off). */
  childChecked: boolean;
  onRootChange: (checked: boolean) => void;
  onChildChange: (checked: boolean) => void;
  /** Disabled while the session record is still loading. */
  disabled?: boolean;
}

export function ContextRagSettingsToggle({
  rootChecked,
  childChecked,
  onRootChange,
  onChildChange,
  disabled = false,
}: ContextRagSettingsToggleProps) {
  const childHint = rootChecked
    ? childChecked
      ? 'Sub-agent runs also retrieve relevant context for their own task. Takes effect on the next run.'
      : 'Sub-agent runs start from parent conclusions only. Turn on to let them retrieve relevant context too. Takes effect on the next run.'
    : 'Turn on run-start retrieval first — sub-agent retrieval requires it.';

  return (
    <>
      <label
        data-testid="context-rag-toggle"
        className="flex w-full cursor-pointer items-center justify-between gap-3 rounded px-2 py-1.5 text-xs hover:bg-accent"
      >
        <Tooltip
          label="At the start of each run, the agent retrieves the most relevant earlier material from this chat and starts with it already in hand. Takes effect on the next run."
          side="left"
          multiline
        >
          <span data-testid="context-rag-toggle-label" className="whitespace-nowrap">
            Retrieve relevant context at run start
          </span>
        </Tooltip>
        <input
          data-testid="context-rag-toggle-input"
          type="checkbox"
          checked={rootChecked}
          disabled={disabled}
          onChange={(e) => onRootChange(e.target.checked)}
          className="cursor-pointer"
        />
      </label>
      <label
        data-testid="context-rag-child-toggle"
        className={`flex w-full items-center justify-between gap-3 rounded px-2 py-1.5 pl-6 text-xs ${
          rootChecked ? 'cursor-pointer hover:bg-accent' : 'cursor-not-allowed opacity-50'
        }`}
      >
        <Tooltip label={childHint} side="left" multiline>
          <span data-testid="context-rag-child-toggle-label" className="whitespace-nowrap">
            Also in sub-agent runs
          </span>
        </Tooltip>
        <input
          data-testid="context-rag-child-toggle-input"
          type="checkbox"
          checked={childChecked}
          disabled={disabled || !rootChecked}
          onChange={(e) => onChildChange(e.target.checked)}
          className="cursor-pointer"
        />
      </label>
    </>
  );
}
